import React, { FC, memo, useCallback, useEffect, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Grid, Typography } from '@mui/material'
import { toast } from 'react-toastify'
import { IAssets } from '@sparling/financial-common'
import { useDispatch, useSelector } from 'react-redux'
import { useStyles } from '../../UserKYC/useStyles'
import { useRefresh, useRest } from '../../../../components/Context'
import { Modal } from '../../../../components/Modal'
import { convertAssetsEnum } from '../../../../utils/createEnum'
import {
  assetsLoading,
  clientTypesForAssignToUserLoading,
  getAssetsData,
  getClientTypesForAssignToUserEnum,
} from '../../../../store/selectors'
import { getAssetsRequest, getClientTypesForAssignToUserRequest } from '../../../../store/actions'
import { SelectWithSearch } from '../../../../components/SelectWithSearch'
import { MultiAssetsSelect } from '../../../../components/MultiSelect'

interface ITypeAssets {
  idSf: string | undefined
  isOpen: boolean
  modalClose: () => void
  clientTypeId: string | null | undefined
  userAssets:
    | Array<{
        id: number
        instrument: string
        label: string
        underlier: string
      }>
    | null
    | undefined
}

type ChangeClientTypeOverrideForm = {
  clientType: string | null | undefined
  additionalAssets: number[]
}

const ChangeClientTypeOverride: FC<ITypeAssets> = ({ idSf, isOpen, modalClose, clientTypeId, userAssets }) => {
  const formMethods = useForm<ChangeClientTypeOverrideForm>({
    mode: 'onBlur',
    defaultValues: {
      clientType: clientTypeId || null,
      additionalAssets: userAssets?.map((asset) => asset.id) || [],
    },
  })
  const classes = useStyles()
  const { rxios } = useRest()
  const { saveIsRefresh } = useRefresh()
  const { handleSubmit, reset, setValue, getValues, control } = formMethods

  const dispatch = useDispatch()
  const loadingClientTypesForAssignToUser = useSelector(clientTypesForAssignToUserLoading)
  const clientTypesForAssignToUser = useSelector(getClientTypesForAssignToUserEnum)
  const assetsIsLoading = useSelector(assetsLoading)
  const { items } = useSelector(getAssetsData)

  const getClientTypes = useCallback(
    (search: string) => {
      dispatch(getClientTypesForAssignToUserRequest({ search }))
    },
    [dispatch],
  )

  const loadAssetsData = useCallback(
    (page = 1) => {
      dispatch(getAssetsRequest({ page }))
    },
    [dispatch],
  )

  useEffect(() => {
    loadAssetsData()
  }, [loadAssetsData])

  const onSubmit = useCallback(
    (data: ChangeClientTypeOverrideForm) => {
      // Ensure we always send both fields, even if they weren't changed
      const formData = {
        clientType: data.clientType || clientTypeId,
        additionalAssets: data.additionalAssets || userAssets?.map((asset) => asset.id) || [],
      }
      rxios.post(`user/${idSf}/change-type-assets`, formData).subscribe(
        // eslint-disable-next-line
        (next: any) => {
          if (next.success) {
            toast.success(`Client Type or groups override was changed.`)
          }
        },
        // eslint-disable-next-line
        (error: any) => {
          toast.error(error.response?.data?.message || error.message)
        },
        () => {
          saveIsRefresh(true)
          modalClose()
        },
      )
    },
    [rxios, saveIsRefresh, modalClose, idSf, clientTypeId, userAssets],
  )

  useEffect(() => {
    if (!isOpen) setTimeout(reset, 300)
  }, [isOpen, reset])

  const selectedClientTypeId = useMemo(() => getValues().clientType ?? null, [getValues])

  const defaultClientTypeId = useMemo(
    () => clientTypesForAssignToUser.find((item) => item.value === clientTypeId) ?? null,
    [clientTypesForAssignToUser, clientTypeId],
  )

  useEffect(() => {
    if (clientTypeId) {
      setValue('clientType', clientTypeId)
    }
    if (userAssets && userAssets.length > 0) {
      setValue(
        'additionalAssets',
        userAssets.map((asset) => asset.id),
      )
    }
  }, [clientTypeId, setValue, userAssets])

  return (
    <Modal title="Edit" isOpen={isOpen} modalClose={modalClose} onSubmit={handleSubmit(onSubmit)} submitTitle={'Save'}>
      <FormProvider {...formMethods}>
        <Grid container>
          <Grid item xs={12} className={classes.field}>
            <Typography component="span">Client Type: </Typography>
            <SelectWithSearch
              inputName={'clientType'}
              width={250}
              mb={0}
              labelName={''}
              placeholder={'Please Select'}
              noOptionsText={'Client types not found'}
              control={control}
              selectedId={selectedClientTypeId}
              loading={loadingClientTypesForAssignToUser}
              options={clientTypesForAssignToUser}
              onSearch={getClientTypes}
              defaultOption={defaultClientTypeId}
            />
          </Grid>

          <Grid item xs={12} className={classes.field}>
            <Typography component="span">Groups Override: </Typography>

            <MultiAssetsSelect
              inputName={'additionalAssets'}
              placeholder={'Select Additional Assets'}
              control={control}
              loading={assetsIsLoading}
              options={convertAssetsEnum(items as IAssets[])}
              firstLoad={loadAssetsData}
              setValue={setValue}
              defaultValue={userAssets?.map((asset) => asset.id) || []}
            />
          </Grid>
        </Grid>
      </FormProvider>
    </Modal>
  )
}

export const MemoChangeClientTypeOverride = memo(ChangeClientTypeOverride)
